import {
  Alert,
  Badge,
  Box,
  Button,
  Grid,
  Header,
  SpaceBetween,
  Spinner,
  Tabs,
  TabsProps,
} from '@amzn/awsui-components-core';
import {
  ContentNode,
  ContentType,
  ContentWorkflow,
  FeedbackResourceType,
  PublicationFeedback,
  WorkflowState,
  Content,
  InlineComment,
  UserCommentType,
} from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import * as UtilApi from '../../../api/UtilsApi';
import { MplServiceClientFactory } from '../../../api/MplServiceClientFactory';
import { Location } from 'history';
import { ContentSchemaSection } from '../../../model/ContentSchemaSection';
import ContentDataForm from '../../content/ContentDataForm';
import { ContentViewerContext } from '../ContentViewerContext';
import PublicationHistory from '../PublicationHistory';
import Byline from './Byline';
import { ContentPageNavigation } from './ContentPageNavigation';
import ContentTitle from './ContentTitle';
import LastPublicationDate from './LastPublicationDate';
import ContentEngagementInfo from '../../content-engagement/ContentEngagementInfo';
import { ContentDataProps } from './ContentData';
import { isInternalUser } from '../../utils/ContentDataUtils';
import { isFeedbackClosed } from '../../utils/DisplayNames';
import ReadonlyContentRightColumn from './ReadonlyContentRightColumn';
import BaseContentData, { BaseContentDataState } from './BaseContentData';
import { ContentFeedback, FeedbackRatingSystem } from '../../feedback-service';
import UpdateRequestsPage from '../../publication/UpdateRequestsPage';
import DraftViewerRole from '../../utils/DraftViewerRole';
import { diffContentsSinglePage, diffContentsMultiPage } from '../../utils/TrackChangesUtils';
import { deepClone } from '../../../api/UtilsApi';
import constants from '../../../common/constants';
import { InlineCommentList } from '../../inline-comment';
import { getText } from '@amzn/awscat-editor';
import BrowserHistory from '../../utils/BrowserHistory';
import { withAppContext } from '../../../contexts/AppContext';
import { CONTENT_VIEWER_BAR_RAISER_PREFIX } from '../../../common/RouterPaths';
import { isInBarRaiserPublishedReview } from '../../../api/ContentUtil';
import { ROOT_SECTION_ID } from '../../../model/Contents';

// URL hash prefix used to deep-link to a specific feedback item in the Update Requests tab.
// e.g. /#feedback-<feedbackId> lands the user on that tab with the feedback scrolled into view.
const FEEDBACK_HASH_PREFIX = 'feedback-';

/**
 * Top-level tabs for the readonly content view.
 * - Latest: the current published/draft content
 * - History: previous publication versions
 * - UpdateRequests: feedback/update requests from readers (Update Requests tab)
 */
export enum ReadViewTab {
  Latest = 'ReadViewLatest',
  History = 'ReadViewHistory',
  UpdateRequests = 'ReadViewUpdateRequests',
}

/**
 * State for ReadonlyContentData, extending the shared BaseContentDataState.
 *
 * Most fields are related to three feature areas:
 *
 * 1. Track-changes diff (peer reviewer workflow):
 *    - viewChange, hasDiff, diffContents, draftContent, mostUpdateContent,
 *      canViewTrackChangesBar, canViewAuthorEditingBar, isLoadingTrackChanges, clickRefresh
 *    These drive the "Author has modified this content" banner and the View Changes / Refresh flow.
 *
 * 2. Bar-raiser diff (bar-raiser review workflow):
 *    - viewBarRaiserDiff, isLoadingBarRaiserDiff, barRaiserDiffContent
 *    Lets bar-raisers compare current content against the last reviewed/published version.
 *
 * 3. Published content feedback:
 *    - publicationFeedbacks, showFeedbackFormInUpdateRequests
 *    Data for the "Update requests" tab with pending feedback badge.
 */
interface ReadonlyContentDataState extends BaseContentDataState {
  publicationFeedbacks?: PublicationFeedback[];
  viewChange?: boolean;
  hasDiff?: boolean;
  isLoadingTrackChanges?: boolean;
  diffContents?: Content;
  draftContent?: Content;
  canViewTrackChangesBar?: boolean;
  canViewAuthorEditingBar?: boolean;
  mostUpdateContent?: Content;
  approvalDisabled?: boolean;
  disableApproveMessage?: string;
  latestVersionNumber?: number;
  clickRefresh?: boolean;
  inlineComments?: InlineComment[];
  viewBarRaiserDiff?: boolean;
  isLoadingBarRaiserDiff?: boolean;
  barRaiserDiffContent?: Content;
  showFeedbackFormInUpdateRequests?: boolean;
}

/**
 * READONLY content view — the full "viewer" page.
 *
 * Renders the whole readonly UI:
 *   - Title + byline + last-published date + thumbs-up / engagement info
 *   - Tabs: Latest / History / Update requests
 *   - Page navigation sidebar (for Guides/Strategies)
 *   - Right column: either ReadonlyContentRightColumn (actions, publish controls)
 *     or InlineCommentList (when reviewer/publisher is viewing comments)
 *   - Track-changes banner at the top (for peer reviewers viewing in-progress edits)
 *   - Bar-raiser diff banner at the top (for bar-raisers comparing versions)
 *
 * This class is meant to be subclassed (see DraftReadonlyContentData).
 * Subclasses override `showDiff` to enable the polling system and
 * `sendWorkflowLockEvent` to acquire the edit lock when the user clicks "Edit".
 */
class ReadonlyContentData extends BaseContentData<ReadonlyContentDataState> {
  private static defaultProps = {
    workflow: new ContentWorkflow(),
    workflowEvents: [],
    workflowHistory: [],
  };

  // Readonly view — editing is always disabled here. Used by BaseContentData utilities
  // (e.g. showContentSectionNavigation) to decide whether to show the help panel.
  protected isEditingEnabled: boolean = false;
  // Whether to poll for author edits in componentDidMount. Overridden by DraftReadonlyContentData
  // to true, so track-changes polling only runs on draft workflows (not published content).
  protected showDiff: boolean = false;
  // Unsubscribe for feedback hash-change listener (cleaned up in componentWillUnmount).
  private hashChangeUnlisten: (() => void) | null = null;
  // Set once updatePublicationFeedbacks has run (e.g. UpdateRequestsPage mounted via a
  // #feedback- deep link or "Report to Author"), so the eager loadPublicationFeedbacks
  // response can't overwrite it with older data if it resolves afterwards.
  private feedbacksUpdated = false;

  constructor(props: ContentDataProps) {
    super(props);
    // Clone content
    const updatedContent = this.cloneContent();
    // Replace epic Id that use title as Id with uuid
    this.updateEpicIds(updatedContent);
    this.historyUnlisten = this.listenBrowserHistory();

    this.state = {
      activeTabId: ReadViewTab.Latest,
      currentPage: updatedContent.contentData,
      expanded: true,
      updatedContent,
      location: { pathname: window?.location?.pathname } as Location,
      publicationFeedbacks: props.publicationFeedbacks,
      isLoading: false,
      viewChange: false,
      hasDiff: false,
      isLoadingTrackChanges: false,
      diffContents: undefined,
      draftContent: deepClone(updatedContent),
      canViewTrackChangesBar: false,
      canViewAuthorEditingBar: false,
      mostUpdateContent: deepClone(updatedContent),
      approvalDisabled: false,
      disableApproveMessage: '',
      latestVersionNumber: this.props.workflow?.latestVersionNumber,
      clickRefresh: false,
      inlineComments: [],
      viewBarRaiserDiff: false,
      isLoadingBarRaiserDiff: false,
      barRaiserDiffContent: undefined,
      showFeedbackFormInUpdateRequests: false,
    };
  }

  public async componentDidMount() {
    const hashTag = window?.location?.hash;
    if (hashTag) {
      // Delay the hash navigation so the DOM elements we want to scroll to
      // have been rendered by React first (otherwise scrollIntoView() no-ops).
      setTimeout(() => {
        this.updateCurrentPageByHashTag(hashTag, this.state.updatedContent);
      }, 500);
    } else {
      // No hash → show the root page's section navigation in the help panel.
      this.showContentSectionNavigation(this.state.updatedContent.contentData, true);
    }

    // Handle URL hash for feedback deep linking (e.g. /#feedback-<id> opens Update Requests tab).
    this.handleFeedbackHashNavigation();

    // Also react to hash changes while the page is open (e.g. clicking a feedback link).
    this.hashChangeUnlisten = BrowserHistory.listen(() => {
      this.handleFeedbackHashNavigation();
    });

    // Fetch publication feedbacks eagerly so the "Update requests" tab badge reflects pending
    // items immediately. Without this, the badge stays empty until UpdateRequestsPage itself
    // mounts and fetches — which only happens once that tab is opened. Skipped when the parent
    // already fetched and passed them in (e.g. ReadOnlyPublicationViewer), to avoid a redundant
    // GET /publication-feedback/:contentId on every page load.
    // @ts-expect-error TS2339 - strict migration
    const { userProfile } = this.props.appContext.authnInfo;
    if (isInternalUser(userProfile) && !this.props.publicationFeedbacks?.length) {
      this.loadPublicationFeedbacks();
    }

    // Start the track-changes polling loop only for subclasses that enable it (Draft workflow).
    // Published/readonly content never polls because its content doesn't change beneath the viewer.
    if (this.showDiff) {
      this.pollWorkflow();
    }
  }

  public async componentDidUpdate(prevProps: ContentDataProps, prevState: BaseContentDataState) {
    // Parent (ContentViewer) re-fetched content → refresh our working copy
    if (this.props.content !== prevProps.content) {
      this.handleContentUpdate();
    }

    // FEEDBACK WORKFLOW: Switch to Update Requests tab when "Report to Author" button is clicked
    // This detects when the parent component sets showFeedbackFormInUpdateRequests from false to true
    // and automatically navigates to the Update Requests tab to show the feedback form
    if (
      this.props.showFeedbackFormInUpdateRequests &&
      !prevProps.showFeedbackFormInUpdateRequests
    ) {
      this.setState({
        activeTabId: ReadViewTab.UpdateRequests,
      });
    }

    // TRACK-CHANGES TOGGLE: Triggered when the user clicks "View Changes" or "Refresh Page"
    // on the track-changes banner (which sets isLoading: true via viewChanges()).
    // This is structured as a state-machine driven by isLoading to ensure the diff swap
    // happens AFTER React has committed the loading spinner (hence the componentDidUpdate trigger
    // instead of handling it inline in the button click).
    if (this.state.isLoading) {
      this.setState({
        approvalDisabled: false,
        disableApproveMessage: '',
      });
      if (!this.state.viewChange) {
        // Switching INTO diff view: replace displayed content with the diff-annotated version
        const diffContent = this.state.diffContents
          ? this.state.diffContents
          : this.state.updatedContent;
        this.setState(
          {
            updatedContent: diffContent,
            currentPage: diffContent.contentData,
            viewChange: !this.state.viewChange,
            clickRefresh: false,
            // Approval is disabled while viewing an outdated version — reviewer must refresh first
            approvalDisabled: true,
            disableApproveMessage: constants.DISABLE_APPROVE_WHEN_OUTDATED_MESSAGE,
          },
          () => {
            // Re-apply hash navigation so URL-linked section is still in view after swap
            const hashTag = window?.location?.hash;
            if (hashTag) {
              this.updateCurrentPageByHashTag(hashTag, this.state.updatedContent);
            } else {
              this.showContentSectionNavigation(this.state.updatedContent.contentData, true);
            }
          },
        );
      } else {
        // Switching OUT of diff view back to "most updated" content.
        // clickRefresh triggers a full page reload below (simpler than re-fetching everything).
        const currentContent = this.state.mostUpdateContent
          ? this.state.mostUpdateContent
          : this.state.updatedContent;
        this.setState(
          {
            viewChange: !this.state.viewChange,
            clickRefresh: true,
          },
          () => {
            const hashTag = window?.location?.hash;
            if (hashTag) {
              this.updateCurrentPageByHashTag(hashTag, currentContent);
            } else {
              this.showContentSectionNavigation(currentContent.contentData, true);
            }
          },
        );
      }
      this.setState({
        isLoading: false,
        isLoadingTrackChanges: false,
      });
    }

    // Full-page reload when exiting diff mode — ensures the reviewer sees the latest
    // content + re-fetched workflow state (simpler than coordinating re-fetches here).
    if (this.state.clickRefresh) {
      window.location.reload();
    }
  }

  public componentWillUnmount() {
    // Clean up both history listeners to avoid leaks / ghost updates on unmounted components
    this.historyUnlisten();
    if (this.hashChangeUnlisten) {
      this.hashChangeUnlisten();
    }
  }

  public render() {
    const {
      coAuthors,
      peerReviewers,
      contentOwner,
      reviewRequestStatus,
      viewerType,
      inlineComments,
      contentId,
      workflowId,
      publicationState,
      isCommentingModeEnabled,
      enableCommentMode,
      reFetchInlineComments,
    } = this.props;
    const { updatedContent, currentPage } = this.state;

    const contentView = this.renderContentReadonlyView();
    const ctx = {
      addNewPage: null,
      addNewSection: null,
      changeCurrentPage: this.pageChanged,
      content: updatedContent,
      currentPage,
      updateContent: null,
      owner: contentOwner,
      coAuthors,
      peerReviewers,
      isEditingEnabled: this.isEditingEnabled,
      viewerType,
      reviewRequestStatus,
      inlineComments,
      contentId,
      workflowId,
      createInlineComment: this.createInlineComment,
      publicationState,
      isCommentingModeEnabled,
      enableCommentMode,
      reFetchInlineComments,
    };

    // @ts-expect-error TS2322 - strict migration
    return <ContentViewerContext.Provider value={ctx}>{contentView}</ContentViewerContext.Provider>;
  }

  // Called when the user clicks "Edit" in the readonly view.
  // Acquires the edit lock (via subclass override), then tells the parent (ContentViewer)
  // to switch into edit mode. Parent will re-render ContentData which will now render
  // the edit view instead of readonly.
  protected enableEditing = async () => {
    const { onEnableEditMode } = this.props;
    await this.sendWorkflowLockEvent();
    if (onEnableEditMode) {
      await onEnableEditMode();
    }
  };

  // HOOK: acquire the workflow edit lock. Overridden by DraftReadonlyContentData to send
  // a LOCK event to the draft workflow. Default is no-op for views that don't support editing.
  protected sendWorkflowLockEvent = async () => {
    return null;
  };

  // Creates a new top-level inline comment thread anchored to selected text within a section.
  // Path identifies the section (e.g. ['architecture']), offsets pin the comment to specific chars.
  // After creating, re-fetches all comments so the UI shows the new one.
  private createInlineComment = async (
    path: string[],
    startOffset: number,
    endOffset: number,
    textData: string,
    commentData: string,
  ) => {
    const { contentId, workflowId } = this.props;
    await this.mplService.createInlineComment(
      path,
      contentId,
      workflowId,
      UserCommentType.Thread,
      commentData,
      textData,
      '',
      startOffset,
      endOffset,
    );
    if (this.props.reFetchInlineComments) {
      await this.props.reFetchInlineComments();
    }
  };

  // Auto-navigate to the Update Requests tab when URL hash is #feedback-<id>.
  // Supports deep-linking from email notifications to a specific feedback item.
  private handleFeedbackHashNavigation = () => {
    const hash = window?.location?.hash?.slice(1);
    if (hash?.startsWith(FEEDBACK_HASH_PREFIX)) {
      this.setState({ activeTabId: ReadViewTab.UpdateRequests });
    }
  };

  // Fetches the LATEST content from the backend and computes a diff against what the
  // reviewer is currently viewing. Called when the reviewer's version is outdated (author
  // has made changes since review began). Result drives the "View Changes" banner.
  // Uses multi-page vs single-page diff algorithms based on content type.
  private async preloadDiff() {
    const { contentId } = this.props;
    const { contentType } = this.props.content.contentMetadata;

    this.setState({ isLoadingTrackChanges: true });

    // Get latest content
    const latestContent = (await this.mplService.getContent(contentId)).content;

    // Get diff
    let diffContented;
    if ([ContentType.Guide, ContentType.Strategy].includes(contentType)) {
      diffContented = diffContentsMultiPage(this.state.updatedContent, latestContent);
    } else {
      diffContented = diffContentsSinglePage(this.state.updatedContent, latestContent);
    }

    this.setState({
      hasDiff: diffContented.hasDiff,
      diffContents: diffContented.newContent,
      isLoadingTrackChanges: false,
      canViewTrackChangesBar: true,
      mostUpdateContent: latestContent,
      // Disable approval while viewing outdated version — reviewer should review latest
      approvalDisabled: true,
      disableApproveMessage: constants.DISABLE_APPROVE_WHEN_OUTDATED_MESSAGE,
    });
  }

  // One iteration of the track-changes polling loop. Two checks:
  //   1. Is the author actively editing right now? (lock is held)
  //      → Show "currently being edited" banner and disable approval
  //   2. Has the author committed changes since reviewer started? (version number bumped)
  //      → Preload the diff and show the "View Changes" banner
  // If neither, clear any existing banners and re-enable approval.
  // Uses `self` alias because it's called via setInterval — `this` would be wrong.
  // @ts-expect-error TS7006 - strict migration
  private async poll(self) {
    const { workflow, contentId } = self.props;

    let currContentWorkflow;
    try {
      currContentWorkflow = (await self.mplService.getContentWorkflow(contentId)).workflow;
    } catch (e) {
      // Swallow errors — polling is best-effort; we'll retry on the next interval.
      return;
    }

    if (currContentWorkflow.lock) {
      // Author currently editing document → block reviewer actions and show banner
      self.setState({
        canViewAuthorEditingBar: true,
        approvalDisabled: true,
        disableApproveMessage: constants.DISABLE_APPROVE_WHEN_EDITING_MESSAGE,
      });
    } else {
      self.setState({
        canViewAuthorEditingBar: false,
        approvalDisabled: false,
        disableApproveMessage: '',
      });
      // If peer reviewer viewing outdated version of content, get content diff
      if (
        workflow?.latestVersionNumber &&
        workflow?.latestVersionNumber < currContentWorkflow.latestVersionNumber
      ) {
        await self.preloadDiff();
      } else {
        self.setState({
          canViewTrackChangesBar: false,
        });
      }
    }
  }

  // Starts the track-changes polling loop for peer reviewers during IN_REVIEW state only.
  // Stops polling when the tab is hidden (document.hidden) to conserve bandwidth/battery.
  // Other roles (Author, Admin, TechWriter) don't need polling because they either ARE the
  // editor or aren't doing time-sensitive review work.
  private async pollWorkflow() {
    const { workflow, draftViewerRole } = this.props;

    // Start polling after peer reviewer accepted review
    if (
      draftViewerRole === DraftViewerRole.Reviewer &&
      workflow?.draftState === WorkflowState.IN_REVIEW
    ) {
      const self = this;
      const intervalPoll = setInterval(async () => {
        if (document.hidden) {
          // Tab hidden → stop polling to conserve bandwidth; user will re-open to refresh anyway
          clearInterval(intervalPoll);
          return;
        }
        await this.poll(self);
      }, constants.POLLING_DELAY);
    }
  }

  private renderContentSection = () => {
    const { updatedContent } = this.state;
    const { contentMetadata } = updatedContent;
    const gridDefinition = [ContentType.Guide, ContentType.Strategy].includes(
      contentMetadata.contentType,
    )
      ? [{ colspan: 3 }, { colspan: 9 }]
      : [{ colspan: 12 }];
    return (
      <Grid gridDefinition={gridDefinition}>
        {this.renderPageNavigation()}
        <div>{this.renderContentDataSections()}</div>
      </Grid>
    );
  };

  private renderPageNavigation = () => {
    const { updatedContent } = this.state;
    const { contentMetadata } = updatedContent;
    const { contentType } = contentMetadata;
    const isGuideOrStrategy = [ContentType.Guide, ContentType.Strategy].includes(contentType);
    return isGuideOrStrategy ? (
      <div>
        <ContentPageNavigation />
      </div>
    ) : null;
  };

  private renderContentDataSections = () => {
    const {
      coAuthors,
      contentId,
      workflow,
      workflowHistory,
      workflowId,
      showTechwriterMetadata,
      showSectionalComments,
      hideEmptySections,
    } = this.props;

    const { updatedContent, expanded } = this.state;
    const { contentMetadata, contentTypeMetadata } = updatedContent;
    const { contentType } = contentMetadata;
    const isGuideOrStrategy = [ContentType.Guide, ContentType.Strategy].includes(contentType);

    return (
      <ContentDataForm
        expanded={expanded}
        contentId={contentId}
        workflowId={workflowId}
        workflow={workflow}
        workflowHistory={workflowHistory}
        coAuthors={coAuthors}
        templateSchema={UtilApi.getContentTemplateSchema(
          contentType,
          contentTypeMetadata,
          ContentSchemaSection.ContentSections,
        )}
        readonly={!this.isEditingEnabled}
        contentPage={this.state.currentPage}
        showTechwriterMetadata={showTechwriterMetadata}
        showPage={isGuideOrStrategy}
        hideEmptyContent={hideEmptySections}
        showSectionalComments={showSectionalComments}
        sectionsSortable={isGuideOrStrategy}
      />
    );
  };

  private pageChanged = async (selectedPage: ContentNode, depth: number) => {
    window.location.hash =
      selectedPage.id === ROOT_SECTION_ID ? '' : selectedPage.href || selectedPage.id;
    this.setState({
      currentPage: selectedPage,
    });
  };

  private updatePublicationFeedbacks = (publicationFeedbacks: PublicationFeedback[]) => {
    this.feedbacksUpdated = true;
    this.setState({ publicationFeedbacks });
  };

  private loadPublicationFeedbacks = async () => {
    const { contentId } = this.props;
    try {
      // Use the v2 client (no Flashbar coupling): this is a passive, best-effort fetch that
      // runs on every readonly/draft page load, so a failure must not surface a global error
      // banner the way this.mplService's (V1 MplServiceClient) decorated method would.
      const { publicationFeedbacks } =
        await MplServiceClientFactory.createV2().listPublicationFeedbacks(contentId);
      // Don't overwrite fresher data if UpdateRequestsPage already reported an update
      // (e.g. opened via a #feedback- deep link or "Report to Author") while this was in flight.
      if (!this.feedbacksUpdated) {
        this.setState({ publicationFeedbacks });
      }
    } catch (e) {
      // Swallow errors — this only refreshes the "Update requests" badge; the tab itself
      // still fetches and surfaces errors when opened.
    }
  };

  // Handler for the "Report to Author" button — switches to Update Requests tab
  // and shows the feedback form so reader can submit an update request.
  protected handleReportToAuthor = () => {
    this.setState({
      showFeedbackFormInUpdateRequests: true,
    });
    this.changeTabView(ReadViewTab.UpdateRequests);
  };

  // @ts-expect-error TS7030 - strict migration
  private handleShowInlineComment = () => {
    const {
      showInlineCommentsInReviewerPage,
      showInlineCommentsInPublisherPage,
      setShowInlineCommentsInReviewerPage,
      setShowInlineCommentsInPublisherPage,
    } = this.props;
    if (showInlineCommentsInReviewerPage) {
      return setShowInlineCommentsInReviewerPage;
    } else if (showInlineCommentsInPublisherPage) {
      return setShowInlineCommentsInPublisherPage;
    }
  };

  private renderContentReadonlyView = () => {
    const {
      coAuthors,
      contentId,
      contentOwner,
      publicationDate,
      publicationState,
      workflow,
      workflowId,
      workflowHistory,
      showInlineCommentsInReviewerPage,
      showInlineCommentsInPublisherPage,
    } = this.props;

    const { updatedContent, activeTabId, isLoading } = this.state;
    const { contentMetadata } = updatedContent;
    const { contentName, contentShortName } = contentMetadata;
    // @ts-expect-error TS2339 - strict migration
    const { userProfile } = this.props.appContext.authnInfo;

    const contentClass = 'awsui-readonly';
    const contentDivId = 'readonly-content-data';

    const contentStateWithFeedback = [
      WorkflowState.DISTRIBUTED,
      WorkflowState.REVOKED,
      WorkflowState.PUBLISHED,
      WorkflowState.FINALIZED,
      WorkflowState.FINALIZING,
    ];
    // @ts-expect-error TS2345 - strict migration
    const contentThumbUpButton = contentStateWithFeedback.includes(publicationState) ? (
      <span>
        <ContentFeedback
          contentId={contentId}
          resourceType={FeedbackResourceType.Content}
          workflowId={workflowId}
          ratingSystem={FeedbackRatingSystem.LikeDislike}
        />
      </span>
    ) : null;
    const contentEngagementInfo =
      // @ts-expect-error TS2345 - strict migration
      contentStateWithFeedback.includes(publicationState) && isInternalUser(userProfile) ? (
        <span>
          <ContentEngagementInfo contentId={contentId} />
        </span>
      ) : null;

    const contentCreator = (
      <div id="meta">
        {/* @ts-expect-error TS2322 - strict migration */}
        <Byline owner={contentOwner} coAuthors={coAuthors} workflow={workflow} />
        <LastPublicationDate
          workflow={workflow}
          publicationDate={publicationDate}
          label={'Last published on'}
        />
        {contentThumbUpButton}
        {contentEngagementInfo}
      </div>
    );

    const readonlyTitle = (
      <SpaceBetween size="m">
        <Header>
          <ContentTitle
            title={contentName}
            shortTitle={contentShortName}
            contentId={contentId}
            workflowId={workflowId}
            readonly={!this.isEditingEnabled}
            stretch
          />
        </Header>
        {contentCreator}
      </SpaceBetween>
    );

    const showInlineComments =
      showInlineCommentsInReviewerPage || showInlineCommentsInPublisherPage;
    const rightColumn = !showInlineComments ? (
      <div>
        <ReadonlyContentRightColumn
          {...this.props}
          content={updatedContent}
          onEnableEditMode={this.enableEditing}
          onTabViewChange={this.changeTabView}
          approvalDisabled={this.state.approvalDisabled}
          disableApproveMessage={this.state.disableApproveMessage}
        />
      </div>
    ) : (
      <InlineCommentList
        contentId={contentId}
        workflowId={workflowId}
        setShowAllComment={this.handleShowInlineComment()}
      />
    );

    const getTabItems = () => {
      const tabItems: TabsProps.Tab[] = [
        {
          label: 'Latest version',
          id: ReadViewTab.Latest,
          content: this.renderContentSection(),
        },
      ];
      // History Tab
      if (workflowHistory?.length) {
        tabItems.push({
          label: 'History',
          id: ReadViewTab.History,
          content: (
            <PublicationHistory
              // @ts-expect-error TS18048 - strict migration
              contentId={workflow.contentId}
              // @ts-expect-error TS18048 - strict migration
              workflowId={workflow.publicationWorkflowId}
              visible
              // @ts-expect-error TS2322 - strict migration
              workflows={this.props.workflowHistory}
            />
          ),
        });
      }

      // Update requests Tab - Display to AWS users only
      if (isInternalUser(userProfile)) {
        // @ts-expect-error TS2532 - strict migration
        const pendingFeedbacks = this.state?.publicationFeedbacks.filter(
          (pf) => !isFeedbackClosed(pf.state),
        );
        const showUpdateNeeded = pendingFeedbacks.length > 0;
        tabItems.push({
          label: (
            <SpaceBetween direction="horizontal" size="xs">
              <div key={'UpdateLabel'}>Update requests</div>
              {showUpdateNeeded && <Badge color="red">{pendingFeedbacks.length}</Badge>}
            </SpaceBetween>
          ),
          id: ReadViewTab.UpdateRequests,
          content: (
            <UpdateRequestsPage
              contentId={contentId}
              // @ts-expect-error TS18048 - strict migration
              ownerId={contentOwner.userId}
              onFeedbacksUpdated={this.updatePublicationFeedbacks}
              initialShowFeedbackForm={
                this.props.showFeedbackFormInUpdateRequests ||
                this.state.showFeedbackFormInUpdateRequests
              }
              highlightFeedbackId={
                window?.location?.hash?.startsWith(`#${FEEDBACK_HASH_PREFIX}`)
                  ? window.location.hash.slice(1 + FEEDBACK_HASH_PREFIX.length)
                  : undefined
              }
            />
          ),
        });
      }
      return tabItems;
    };

    const contentTab = this.props.showTabView ? (
      <Tabs
        tabs={getTabItems()}
        activeTabId={activeTabId}
        ariaLabel="Content view navigation"
        // @ts-expect-error TS2322 - strict migration
        onChange={this.handleTabChange}
      />
    ) : (
      this.renderContentSection()
    );

    const viewChanges = () => {
      this.setState({ isLoading: true });
    };

    if (isLoading) {
      return <Spinner />;
    }

    const authorEditingBar = (
      <Alert>
        You are currently unable to complete approval or add new inline comments while the author
        makes changes.
      </Alert>
    );
    const barRaiserViewDiffBar = this.renderBarRaiserDiffViewBar();

    const topBar = this.state.canViewAuthorEditingBar ? (
      authorEditingBar
    ) : this.state.canViewTrackChangesBar ? (
      this.state.hasDiff ? (
        <Alert
          action={
            <Button
              ariaLabel={this.state.viewChange ? 'Refresh page' : 'View changes'}
              onClick={viewChanges}
            >
              {this.state.viewChange ? 'Refresh Page' : 'View Changes'}
            </Button>
          }
        >
          Author has modified this content
        </Alert>
      ) : (
        <Alert>Author has not modified this content</Alert>
      )
    ) : null;

    const contentView =
      activeTabId === ReadViewTab.Latest ? (
        <Grid
          className="width-full"
          gridDefinition={[
            { colspan: { default: 12, xs: 9 } },
            { colspan: { default: 12, xs: 3 } },
          ]}
        >
          <div>
            {this.state.isLoadingTrackChanges ? <Spinner /> : topBar}
            {this.state.isLoadingBarRaiserDiff ? <Spinner /> : barRaiserViewDiffBar}
            {readonlyTitle}
            {contentTab}
          </div>
          <div>{rightColumn}</div>
        </Grid>
      ) : (
        <div className="width-full">
          {readonlyTitle}
          {contentTab}
        </div>
      );
    const readOnlyView = (
      <div>
        <Box variant="div" padding={{ top: 'm' }}>
          <div className={contentClass} id={contentDivId}>
            {contentView}
          </div>
        </Box>
      </div>
    );
    return readOnlyView;
  };

  // BAR-RAISER DIFF BAR: Shown only in the bar-raiser review URL path.
  // Lets bar-raisers compare the current content against:
  //   - the last REJECTED version (if rejectVersionNumber >= 0), or
  //   - the last PUBLISHED version (if this is an update to an existing publication).
  // Returns null in non-bar-raiser contexts and on first-time publications.
  private renderBarRaiserDiffViewBar = () => {
    const { workflow } = this.props;
    const { viewBarRaiserDiff } = this.state;
    // @ts-expect-error TS2339 - strict migration
    const { rejectVersionNumber, draftWorkflowId } = workflow;
    if (!window?.location?.pathname?.includes(CONTENT_VIEWER_BAR_RAISER_PREFIX)) {
      return null;
    }
    // allow show diff option when content was previsouly published or rejected
    if (
      rejectVersionNumber >= 0 ||
      (draftWorkflowId > 1 && !isInBarRaiserPublishedReview(workflow))
    ) {
      const actionBtn = (
        <Button
          ariaLabel={viewBarRaiserDiff ? 'Hide changes' : 'View changes'}
          onClick={this.handleViewBarRaiserDiffBar}
        >
          {viewBarRaiserDiff ? 'Hide changes' : 'View changes'}
        </Button>
      );
      return (
        <Alert action={actionBtn}>
          Author has modified this content since last{' '}
          {rejectVersionNumber >= 0 ? 'review.' : 'publish.'}
        </Alert>
      );
    }
    return null;
  };

  // Toggles the bar-raiser diff view on/off.
  // ON:  fetches the previous reviewed/published version, computes diff, shows in viewer.
  // OFF: restores the original content from props.
  // Choice of comparison version:
  //   - If previous review was a rejection, diff against that rejected version.
  //   - Otherwise (content was previously published), diff against last publication.
  private handleViewBarRaiserDiffBar = async () => {
    const { contentId, workflow, content } = this.props;
    const { contentType } = content.contentMetadata;
    // @ts-expect-error TS2339 - strict migration
    const { rejectVersionNumber, draftWorkflowId } = workflow;
    if (!this.state.viewBarRaiserDiff) {
      // show diff between last reviewed/published content vs latest content
      this.setState({
        viewBarRaiserDiff: true,
        isLoadingBarRaiserDiff: true,
      });
      let previousVersionContent;
      if (rejectVersionNumber >= 0) {
        previousVersionContent = await this.mplService.getContentWithVersion(
          contentId,
          rejectVersionNumber,
        );
      } else if (draftWorkflowId > 1) {
        previousVersionContent = await this.mplService.getPublication(contentId);
      } else {
        // No previous version to compare — this shouldn't happen because renderBarRaiserDiffViewBar
        // only shows the button when a previous version exists.
        // tslint:disable-next-line: no-console
        console.error('No previous version to compare.');
      }
      let diffContent;
      if ([ContentType.Guide, ContentType.Strategy].includes(contentType)) {
        // @ts-expect-error TS18048 - strict migration
        diffContent = diffContentsMultiPage(previousVersionContent.content, this.props.content);
      } else {
        // @ts-expect-error TS18048 - strict migration
        diffContent = diffContentsSinglePage(previousVersionContent.content, this.props.content);
      }
      this.setState({
        isLoadingBarRaiserDiff: false,
        updatedContent: diffContent.newContent,
        currentPage: diffContent.newContent.contentData,
      });
    } else {
      // Toggling OFF → restore the original (non-diffed) content
      this.setState({
        viewBarRaiserDiff: false,
        updatedContent: content,
        currentPage: content.contentData,
      });
    }
  };
}

export default ReadonlyContentData;
